/**
 * Markdown schreiben, mit Vorschau — wie bei GitHub (T-0160).
 *
 * Unten liegt das gewohnte Textfeld mit `@`-Erwähnungen (`MentionTextarea`),
 * darüber eine schmale Leiste für das Nötigste: fett, kursiv, Überschrift,
 * Listen, Link, Code. Wer Markdown kennt, tippt es einfach; die Leiste ist für
 * alle anderen. „Vorschau“ zeigt den Text so, wie er im Ticket erscheint.
 */

import { useRef, useState, type TextareaHTMLAttributes } from 'react';
import { Bold, Code, Heading, Italic, Link2, List, ListOrdered, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { linkEinsetzen, umschliessen, zeilenPraefix, type Auswahl } from '../../lib/markdown';
import { MarkdownText } from './markdown-text';
import { MentionTextarea, type ErwaehnungsOptionen } from './mention-textarea';

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> &
  Partial<Omit<ErwaehnungsOptionen, 'personen'>> & {
    value: string;
    onChange: (wert: string) => void;
    personen?: ErwaehnungsOptionen['personen'];
  };

type Werkzeug = { titel: string; icon: LucideIcon; anwenden: (a: Auswahl) => Auswahl };

const WERKZEUGE: Werkzeug[] = [
  { titel: 'Fett', icon: Bold, anwenden: (a) => umschliessen(a, '**') },
  { titel: 'Kursiv', icon: Italic, anwenden: (a) => umschliessen(a, '_') },
  { titel: 'Überschrift', icon: Heading, anwenden: (a) => zeilenPraefix(a, '### ') },
  { titel: 'Liste', icon: List, anwenden: (a) => zeilenPraefix(a, '- ') },
  { titel: 'Nummerierte Liste', icon: ListOrdered, anwenden: (a) => zeilenPraefix(a, '', true) },
  { titel: 'Link', icon: Link2, anwenden: linkEinsetzen },
  { titel: 'Code', icon: Code, anwenden: (a) => umschliessen(a, '`') },
];

export function MarkdownEditor({
  value,
  onChange,
  personen = [],
  verweise,
  praefix,
  ohneTicket,
  className,
  rows = 6,
  ...rest
}: Props) {
  const feld = useRef<HTMLTextAreaElement | null>(null);
  const [vorschau, setVorschau] = useState(false);

  const anwenden = (werkzeug: Werkzeug) => {
    const el = feld.current;
    const auswahl = { text: value, start: el?.selectionStart ?? value.length, ende: el?.selectionEnd ?? value.length };
    const neu = werkzeug.anwenden(auswahl);
    onChange(neu.text);
    // Nach dem Rendern Fokus und Auswahl zurück ins Feld.
    requestAnimationFrame(() => {
      feld.current?.focus();
      feld.current?.setSelectionRange(neu.start, neu.ende);
    });
  };

  return (
    <div className={cn('rounded-md border', className)}>
      <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1">
        {(['schreiben', 'vorschau'] as const).map((reiter) => (
          <button
            key={reiter}
            type="button"
            aria-pressed={(reiter === 'vorschau') === vorschau}
            onClick={() => setVorschau(reiter === 'vorschau')}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium',
              (reiter === 'vorschau') === vorschau ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {reiter === 'schreiben' ? 'Schreiben' : 'Vorschau'}
          </button>
        ))}
        {!vorschau && (
          <div className="ml-auto flex items-center gap-0.5">
            {WERKZEUGE.map((werkzeug) => (
              <button
                key={werkzeug.titel}
                type="button"
                title={werkzeug.titel}
                aria-label={werkzeug.titel}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => anwenden(werkzeug)}
                className="rounded p-1.5 text-muted-foreground hover:bg-background hover:text-foreground"
              >
                <werkzeug.icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        )}
      </div>
      {vorschau ? (
        <div className="min-h-[6rem] px-3 py-2 text-sm">
          {value.trim() ? (
            <MarkdownText text={value} personen={personen} verweise={verweise} praefix={praefix} ohneTicket={ohneTicket} />
          ) : (
            <p className="text-muted-foreground">Nichts zu zeigen.</p>
          )}
        </div>
      ) : (
        <MentionTextarea
          ref={feld}
          value={value}
          onChange={onChange}
          personen={personen}
          rows={rows}
          className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          {...rest}
        />
      )}
    </div>
  );
}
